
/* ══════════════════════════════════════════════════════════════════════
   <reading-quiz> — branching reading-stage quiz.
   ══════════════════════════════════════════════════════════════════════ */

/* Questions, keyed by id. opt = [label, value]. */
const Q = {
  age: { q:"What's your child's age?",
    a:[["Toddler (1-3)","toddler"],["Pre-K (3-5)","prek"],["Kinder (5-6)","kinder"],["Primary (6+)","primary"]] },

  level: { q:"Which of the following best describes their reading level?",
    a:[["Have not started yet","notstarted"],["Learning the basic letter sounds","learning"],
       ["Knows most sounds but can’t blend","sounds"],["Starting to read basic CVC words","starting"],
       ["Reading slowly but not fluent","slowly"],["Reading most words confidently","mostwords"]] },

  pronounce: { q:"Can your toddler pronounce most of the letter sounds (ex: the /m/ in mama, or /s/ in snake)?",
    a:[["Yes","yes"],["No","no"]] },

  remember: { q:"Can your toddler remember simple objects/things/names from day to day (e.g., couch, dog, moo)",
    a:[["Yes","yes"],["No","no"]] },

  confidence: { q:"On a scale of 1–4, how confident are you in your ability to help your child learn to read?",
    a:[["Very confident","4"],["Somewhat confident","3"],["Not very confident","2"],["Not confident at all","1"]] },

  country: { q:"Last step! What country do you live in?",
    a:[["USA","USA"],["Canada","CA"],["UK","UK"],["Australia","AU"],["Singapore","SG"],["Other","Other"]] },
};

/* Display label for a stored option value (e.g. "toddler" → "Toddler (1-3)"). */
function labelFor(qid, value){
  const opt = Q[qid] && Q[qid].a.find(o => o[1] === value);
  return opt ? opt[0] : (value || "");
}

/* The ordered path of question ids, decided by the age answer. */
function pathFor(age){
  const tail = ["confidence","country"];
  if(age === "toddler") return ["age","pronounce","remember",...tail];
  return ["age","level",...tail];           // pre-k / kinder / primary
}

/* Result is set by age + readiness/level. Confidence + country don't affect it. */
function computeResult(a){
  if(a.age === "toddler"){
    return (a.pronounce === "no" || a.remember === "no") ? "Not Ready Yet" : "Ready To Start";
  }
  switch(a.level){
    case "notstarted":
    case "learning":  return "Learning Letter Sounds";
    case "sounds":
    case "starting":  return "Learning to Blend";
    case "slowly":    return "Building Reading Fluency";
    case "mostwords": return "Already Reading Well";
  }
}

const RESULTS = {
  "Not Ready Yet":
    "Your little one is still laying the very first groundwork — and that’s completely normal at this age. The best thing right now is playful time with sounds and language, no pressure. We’ll show you exactly how.",
  "Ready To Start":
    "Your toddler is showing the early signs of readiness — they can hold sounds and names in mind. This is the perfect window to start gently building letter sounds.",
  "Learning Letter Sounds":
    "Your child is at the letter-sounds stage — connecting each letter to the sound it makes. Get this foundation solid and reading starts to click. Here’s the step-by-step.",
  "Learning to Blend":
    "Your child knows their sounds and is ready for the big leap: blending them into whole words (c-a-t → cat). This is where it starts to feel like real reading.",
  "Building Reading Fluency":
    "Your child is reading — now it’s about smoothing it out: pace, confidence, and a growing bank of sight words so reading begins to feel effortless.",
  "Already Reading Well":
    "Your child is reading independently — wonderful. Now we keep the momentum with richer stories and deeper comprehension so they keep climbing.",
};

/* CTA on the result screen (after the email is captured). Each result links to its OWN
   result page. These are the REAL published page handles (not ?view= editor previews). */
const RESULT_CTA = {
  label: "See your child's plan",
  // Each result → its own published result page (real handles confirmed from the
  // theme editor preview paths). NOTE: "advance-reader" has no "d" — matches the page
  // handle, not the template name (new-quiz-advanced-reader).
  urls: {
    "Not Ready Yet":            "/pages/quiz-not-ready",
    "Ready To Start":           "/pages/quiz-ready-to-start",
    "Learning Letter Sounds":   "/pages/quiz-learn-sounds",
    "Learning to Blend":        "/pages/quiz-blending-words",
    "Building Reading Fluency": "/pages/quiz-advanced-sounds",
    "Already Reading Well":     "/pages/quiz-advance-reader",
  },
};

/* ── KLAVIYO (client-side, browser-safe) ───────────────────────────────
   Browser-safe /client endpoints — authenticate with your PUBLIC key only
   (6-char Site / Company ID). NEVER put a private (pk_…) key here.

   Each result routes to its OWN list — fill in the 6 IDs below. On submit:
     1) the parent is subscribed (with email-marketing consent) to the list
        for THEIR result, and
     2) a "Completed Reading Quiz" event fires with result + answers — use it
        as a Flow trigger, and/or to build dynamic Segments on reading_stage.
   IDs: publicKey → Settings › API keys › "Public API key (Site ID)";
        each list ID → open the List, it's the short code in the URL ("RxAbC2"). */
const KLAVIYO = {
  publicKey: "QVfZ9M",            // public Site ID (browser-safe; never the private pk_ key)
  revision:  "2026-04-15",
  source:    "Interact Quiz",     // saved as the profile "$source" — merges with your existing
                                  // Interact-quiz profiles (change to distinguish the new quiz).
  inFlow:    "Starting Point Quiz", // saved as the "In Flow" property — marks which quiz they took.
  listId:    "Y7vSBG",            // "New Quiz" — EVERY quiz-taker joins it; your Klaviyo Flows
                                  // route by the "Quiz Starting Point Result" profile property.
};

async function submitLead({ email, result, answers }){
  // Profile properties — exact keys + value formats from a real Interact-synced profile, so
  // this data merges cleanly with quiz-takers already in Klaviyo. "In Flow" marks which quiz.
  const props = {
    "$source":                    KLAVIYO.source,
    "Accepts Marketing":          true,
    "In Flow":                    KLAVIYO.inFlow,                             // "Starting Point Quiz"
    "Child Age":                  labelFor("age", answers.age),               // "Kinder (5-6)"
    "Country":                    answers.country,                            // "CA"
    "Parent's Confidence":        labelFor("confidence", answers.confidence), // "Not very confident"
    "Quiz Starting Point Result": result,                                     // "Learning Letter Sounds"
  };

  // Attribute signups to their original ad when the quiz URL contains UTMs.
  // Organic visitors without UTMs retain the existing signup behavior.
  const utmParams = new URLSearchParams(window.location.search);
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
    const value = utmParams.get(key);
    if (value) props[key] = value.slice(0, 255);
  }

  if(KLAVIYO.publicKey.startsWith("YOUR_") || KLAVIYO.listId.startsWith("YOUR_")){
    console.warn("[reading-quiz] Klaviyo not configured — lead not sent.", { email, props });
    return false;                                       // no lead was accepted
  }

  // One call: subscribe to the "New Quiz" list (this triggers your result Flows) + email-
  // marketing consent + all profile properties. The Flows route by the result property.
  try {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${KLAVIYO.publicKey}`, {
      method: "POST",
      headers: { "Content-Type": "application/vnd.api+json", "revision": KLAVIYO.revision },
      body: JSON.stringify({ data: { type: "subscription", attributes: {
        custom_source: KLAVIYO.source,
        profile: { data: { type: "profile", attributes: {
          email, properties: props,
          // consent MUST live INSIDE the profile — Klaviyo 400s if it's a sibling of `profile`
          subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
        } } },
      }, relationships: { list: { data: { type: "list", id: KLAVIYO.listId } } } } }),
    });
    if(!res.ok){
      console.error("[reading-quiz] Klaviyo subscribe failed", res.status, await res.text());
      return false;
    }
    return true; // Klaviyo accepted the subscription request
  } catch(err){
    console.error("[reading-quiz] Klaviyo request failed", err);
    return false;
  }
}

// Only report a quiz lead once Klaviyo has accepted the email submission.
// Shopify's app pixels run in a sandbox and may not expose fbq() to theme scripts.
// Publish a custom Shopify customer event for a pixel integration to consume;
// also send Meta's standard Lead event when an on-page Meta pixel is available.
async function trackAcceptedQuizLead(result){
  const details = { content_name: "Starting Point Quiz", quiz_result: result };
  if(window.Shopify?.analytics?.publish){
    try {
      await window.Shopify.analytics.publish("tcr:starting_point_quiz_lead", details);
    } catch(err){
      console.warn("[reading-quiz] Shopify lead event could not be published", err);
    }
  }
  if(typeof window.fbq === "function"){
    try {
      window.fbq("trackSingle", "6520773784656718", "Lead", details);
    } catch(err){
      console.warn("[reading-quiz] Meta Lead event could not be sent", err);
    }
  }
}
/* ──────────────────────────────────────────────────────────────────── */

const CSS = `
  :host{display:block}
  *{box-sizing:border-box;margin:0;padding:0;font-family:var(--font-body,'DM Sans',sans-serif)}
  .card{background:var(--surface,#fff);border:1px solid var(--line,#E6DCC9);
    border-radius:var(--radius-card,24px);padding:clamp(24px,3.4vw,34px);max-width:440px;margin-inline:auto;
    box-shadow:0 1px 2px rgba(45,34,26,.05),0 30px 60px -32px rgba(45,34,26,.45)}

  .track{height:6px;border-radius:99px;background:var(--bg-alt,#EFE7D7);overflow:hidden;margin-bottom:24px}
  .track .fill{height:100%;border-radius:99px;background:var(--progress,var(--display,#2B4858));
    transition:width .4s cubic-bezier(.22,1,.36,1)}

  .step{font-size:11px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;
    color:var(--muted,#7C746A);font-variant-numeric:tabular-nums}
  .res-label{font-size:11px;font-weight:500;letter-spacing:.14em;text-transform:uppercase;
    color:var(--accent,#C2613B);margin-bottom:14px}
  .q{font-family:var(--font-display,'Fraunces',serif);font-weight:700;
    color:var(--display,#2B4858);font-size:clamp(1.5rem,3.1vw,1.95rem);line-height:1.1;
    letter-spacing:-.3px;text-wrap:balance;margin-bottom:24px}

  .opts{display:flex;flex-direction:column;gap:12px}
  .opt{display:flex;align-items:center;gap:14px;width:100%;text-align:left;cursor:pointer;
    background:var(--surface,#fff);border:1.5px solid var(--line,#E6DCC9);border-radius:14px;
    padding:16px 18px;font-size:1rem;font-weight:400;color:var(--ink,#3D3530);
    box-shadow:0 1px 3px rgba(45,34,26,.05);
    transition:border-color .16s,background-color .16s,box-shadow .16s}
  .opt:hover{border-color:color-mix(in srgb,var(--display,#2B4858) 30%,transparent)}
  .opt .dot{flex:0 0 auto;width:22px;height:22px;border-radius:50%;
    border:2px solid color-mix(in srgb,var(--muted,#7C746A) 55%,transparent);
    display:grid;place-items:center;transition:border-color .16s}
  .opt .dot::after{content:"";width:10px;height:10px;border-radius:50%;background:transparent;
    transition:background-color .16s}
  .opt[aria-checked="true"]{background:var(--surface,#fff);border-color:var(--display,#2B4858);
    box-shadow:0 6px 16px -8px rgba(43,72,88,.4)}
  .opt[aria-checked="true"] .dot{border-color:var(--display,#2B4858)}
  .opt[aria-checked="true"] .dot::after{background:var(--display,#2B4858)}
  .opt:focus-visible{outline:2px solid var(--accent,#C2613B);outline-offset:2px}

  .foot{display:flex;align-items:center;justify-content:space-between;gap:14px;
    margin-top:24px;padding-top:18px;border-top:1px solid var(--line,#E6DCC9)}
  .nav{display:flex;align-items:center;gap:18px}
  .back{appearance:none;border:0;background:none;cursor:pointer;font-weight:500;font-size:.95rem;
    color:var(--muted,#7C746A);display:inline-flex;align-items:center;gap:.3em;transition:color .16s,gap .16s}
  .back:hover{color:var(--display,#2B4858);gap:.5em}
  .next{appearance:none;border:0;background:none;cursor:pointer;font-weight:600;font-size:1rem;
    color:var(--display,#2B4858);display:inline-flex;align-items:center;gap:.4em;
    transition:opacity .16s,gap .16s}
  .next:hover{gap:.7em}
  .next[disabled]{opacity:.32;cursor:not-allowed}

  /* result */
  .stage{font-family:var(--font-display,'Fraunces',serif);font-weight:800;
    color:var(--display,#2B4858);font-size:clamp(2rem,4.4vw,2.7rem);line-height:1.02;
    letter-spacing:-.4px;margin-bottom:14px}
  .blurb{color:var(--ink,#3D3530);opacity:.9;font-size:1.02rem;line-height:1.55;
    margin-bottom:24px;text-wrap:pretty}
  .form{display:flex;flex-direction:column;gap:10px}
  .form label{font-size:.92rem;color:var(--ink,#3D3530);font-weight:500}
  .form input{font-family:inherit;font-size:1rem;padding:15px 16px;border-radius:12px;
    border:1.5px solid var(--line,#E6DCC9);background:var(--bg,#F6F1E7);color:var(--ink,#3D3530)}
  .form input:focus{outline:0;border-color:var(--display,#2B4858);background:#fff}
  .submit{font-family:inherit;font-weight:500;font-size:1rem;color:var(--brand-ink,#F5EFE4);
    background:var(--btn-bg,var(--display,#2B4858));border:0;border-radius:var(--radius-btn,8px);
    padding:16px 22px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;
    gap:.5em;box-shadow:0 10px 24px -6px rgba(43,72,88,.38);transition:background-color .2s,transform .12s}
  .submit:hover{background:#223c4b}
  .submit:active{transform:scale(.98)}
  .submit[disabled]{opacity:.6;cursor:default}
  .consent{font-size:.76rem;color:var(--muted,#7C746A);line-height:1.4;margin-top:2px}
  .done{background:color-mix(in srgb,var(--sage,#7FB89A) 18%,transparent);
    border:1px solid color-mix(in srgb,var(--sage,#7FB89A) 45%,transparent);border-radius:12px;
    padding:16px;color:var(--display,#2B4858);font-size:.96rem;line-height:1.5}
  .restart{margin-top:18px;background:none;border:0;cursor:pointer;color:var(--muted,#7C746A);
    font-size:.85rem;font-weight:500;text-decoration:underline;text-underline-offset:3px}
  .cta{display:flex;width:100%;text-decoration:none;margin-top:2px}
  .gate-back{margin-top:14px}

  /* gentle fade/slide as each step appears */
  @keyframes qin{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
  .anim{animation:qin .35s cubic-bezier(.22,1,.36,1) both}
  .opts.anim,.blurb.anim{animation-delay:.04s}
  .foot.anim,.form.anim{animation-delay:.08s}

  /* respect "reduce motion" — kill all movement */
  @media (prefers-reduced-motion: reduce){
    .track .fill{transition:none}
    .anim{animation:none}
    .opt,.back,.submit,.dot,.dot::after{transition:none}
  }
`;

class ReadingQuiz extends HTMLElement {
  constructor(){
    super();
    this.attachShadow({mode:"open"});
    this.cur = "age";        // current question id (explicit — selecting never auto-advances)
    this.answers = {};       // { questionId: value }
    this.history = [];       // previous question ids, for Back
    this.email = "";         // captured at the gate, before the result is revealed
    this.phase = "quiz";     // "quiz" → "email" (gate) → "result"
  }
  connectedCallback(){
    // Build the shell ONCE so the progress bar can animate — the .fill node
    // persists between steps; only .body is re-rendered each question.
    this.shadowRoot.innerHTML = `<style>${CSS}</style>
      <div class="card">
        <div class="track" role="progressbar" aria-label="Quiz progress"
             aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><div class="fill"></div></div>
        <div class="body"></div>
      </div>`;
    this.fill  = this.shadowRoot.querySelector(".fill");
    this.track = this.shadowRoot.querySelector(".track");
    this.body  = this.shadowRoot.querySelector(".body");
    this.mounted = false;        // don't steal focus on first paint
    this.render();
    this.mounted = true;
  }

  setProgress(pct){
    this.fill.style.width = pct + "%";
    this.track.setAttribute("aria-valuenow", String(pct));
  }

  // Move focus (for keyboard / screen-reader users) without scrolling the page.
  focusEl(sel){
    if(!this.mounted) return;
    const el = this.body.querySelector(sel);
    if(el) el.focus({ preventScroll: true });
  }

  render(){
    if(this.phase === "result"){ return this.renderResult(); }
    if(this.phase === "email"){ return this.renderEmailGate(); }

    const id = this.cur;
    const def = Q[id];
    const chosen = this.answers[id];
    const path = pathFor(this.answers.age);                   // older path is the safe default
    const total = this.answers.age != null ? path.length : null;  // known once age is picked
    const step = (path.indexOf(id) + 1) || 1;
    const completed = step - 1;                               // questions FINISHED, not the current one
    const pct = Math.max(6, Math.round((completed / (total || 5)) * 100)); // tiny sliver at start; only the email gate hits 100%
    this.setProgress(pct);

    const activeIdx = Math.max(0, def.a.findIndex(o => o[1] === chosen));

    this.body.innerHTML = `
        <h2 class="q anim" id="rq-q">${def.q}</h2>
        <div class="opts anim" role="radiogroup" aria-labelledby="rq-q">
          ${def.a.map((opt,i)=>`
            <button class="opt" role="radio" aria-checked="${chosen===opt[1]}" data-v="${opt[1]}"
                    tabindex="${i===activeIdx?0:-1}">
              <span class="dot"></span><span>${opt[0]}</span>
            </button>`).join("")}
        </div>
        <div class="foot anim">
          <span class="step">${total ? `${step} of ${total}` : `Question ${step}`}</span>
          ${this.history.length ? `<button class="back" data-act="back">← Back</button>` : `<span></span>`}
        </div>`;

    const opts = [...this.body.querySelectorAll(".opt")];
    opts.forEach(b => b.addEventListener("click", () => this.choose(id, b.dataset.v)));

    // Arrow keys move focus only; selecting stays an explicit click/Enter so it plays nice with auto-advance.
    this.body.querySelector(".opts").addEventListener("keydown", (e)=>{
      let i = opts.indexOf(this.shadowRoot.activeElement);
      if(i === -1) return;
      if(e.key === "ArrowDown" || e.key === "ArrowRight") i = (i + 1) % opts.length;
      else if(e.key === "ArrowUp" || e.key === "ArrowLeft") i = (i - 1 + opts.length) % opts.length;
      else return;
      e.preventDefault();
      opts.forEach((o,j)=> o.tabIndex = j === i ? 0 : -1);
      opts[i].focus();
    });

    const back = this.body.querySelector('[data-act="back"]');
    if(back) back.addEventListener("click", ()=>{
      if(this.advTimer){ clearTimeout(this.advTimer); this.advTimer = null; }
      const prev = this.history.pop();
      if(prev !== undefined){ this.cur = prev; }
      this.phase = "quiz";
      this.render();
    });

    this.focusEl(".opt[tabindex='0']");   // land on the active option (question announced via aria-labelledby)
  }

  // Select an answer → brief highlight → auto-advance to the next step.
  choose(id, value){
    if(this.advTimer) return;                            // ignore taps while one is already advancing
    this.answers[id] = value;
    this.body.querySelectorAll(".opt").forEach(b =>
      b.setAttribute("aria-checked", String(b.dataset.v === value)));
    this.advTimer = setTimeout(()=>{ this.advTimer = null; this.advance(id); }, 280);
  }

  advance(id){
    const p = pathFor(this.answers.age);
    const nxt = p[p.indexOf(id) + 1] || null;
    this.history.push(id);
    if(nxt === null){ this.phase = "email"; } else { this.cur = nxt; }   // last question → email gate
    this.render();
  }

  // Email gate — REQUIRED before the result is revealed (Interact lead-gen pattern).
  // This is where the lead is captured + sent to Klaviyo.
  renderEmailGate(){
    this.setProgress(100);
    this.body.innerHTML = `
        <p class="res-label anim">Your result is ready</p>
        <h2 class="q anim" id="rq-q" tabindex="-1">Where should we send your child's reading plan?</h2>
        <p class="blurb anim">Enter your email to reveal your child's reading stage — plus the step-by-step plan to move them forward.</p>
        <form class="form anim">
          <input id="rq-email" type="email" required placeholder="you@email.com" autocomplete="email">
          <button class="submit" type="submit">Reveal my result <span aria-hidden="true">→</span></button>
          <p class="consent">Your reading plan plus occasional tips, by email. Unsubscribe anytime.</p>
        </form>
        <button class="back gate-back" data-act="back">← Back</button>`;

    this.body.querySelector(".form").addEventListener("submit", async (e)=>{
      e.preventDefault();
      this.email = this.body.querySelector("#rq-email").value.trim();
      const btn = this.body.querySelector(".submit");
      btn.disabled = true; btn.textContent = "Revealing…";
      const result = computeResult(this.answers);
      const accepted = await submitLead({ email: this.email, result, answers:{...this.answers} });
      if(accepted) await trackAcceptedQuizLead(result);
      const url = RESULT_CTA.urls[result];
      if(url && !url.includes("{{")) window.location.assign(url);   // → the matching result landing page
      else { this.phase = "result"; this.render(); }                // fallback: inline result if URL missing
    });

    const back = this.body.querySelector('[data-act="back"]');
    if(back) back.addEventListener("click",()=>{
      const prev = this.history.pop();
      if(prev !== undefined){ this.cur = prev; }
      this.phase = "quiz";
      this.render();
    });

    this.focusEl("#rq-q");
  }

  renderResult(){
    const result = computeResult(this.answers);
    this.setProgress(100);
    this.body.innerHTML = `
        <p class="res-label anim">Your child's reading stage</p>
        <h2 class="stage anim" tabindex="-1">${result}</h2>
        <p class="blurb anim">${RESULTS[result] || ""}</p>
        <div class="done anim"><strong>Your step-by-step plan is on its way${this.email ? ` to ${this.email}` : ""}.</strong> Check your inbox in a few minutes.</div>
        <a class="submit cta" href="${RESULT_CTA.urls[result] || '#'}">${RESULT_CTA.label} <span aria-hidden="true">→</span></a>
        <button class="restart">↺ Start over</button>`;

    this.body.querySelector(".restart").addEventListener("click",()=>{
      this.answers = {}; this.history = []; this.phase = "quiz"; this.cur = "age"; this.email = ""; this.render();
    });

    this.focusEl(".stage");
  }
}
customElements.define("reading-quiz", ReadingQuiz);